import { Select, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomSelectProps } from '../../interfaces/select';
import '../../styles.scss';
import DropdownArrow from '../DropdownArrow';
import { CustomLoadingOutlined, LabelWrapper, RadioWrapper } from './styles';
import './styles.scss';

const { Option } = Select;

const CustomSelect: React.FC<CustomSelectProps> = props => {
  const { t } = useTranslation();

  const {
    mode,
    empty,
    width,
    style,
    value,
    options = [],
    loading,
    children,
    onChange,
    className,
    popupClassName,
    suffixIcon,
    placeholder,
    suffixColor,
    defaultValue,
    open = false,
    type = 'primary',
    popupMatchSelectWidth = false,
    placement = 'bottomRight',
    showSearch = true,
    onDropdownVisibleChange = (value: boolean) => { },
    callbackOnChange,
    ...rest
  } = props;

  const newStyle = { ...style, width };

  const newPlaceHolder = loading
    ? t('design.loading')
    : placeholder || t('design.select');

  const [checked, setChecked] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(open);

  const color = useMemo(() => {
    if (suffixColor) {
      return suffixColor;
    }
    switch (type) {
      case 'dark':
        return 'var(--secondary)';
      case 'primary':
        return 'var(--primary)';
      case 'linkage':
        return 'var(--link)';
      default:
        return 'var(--primary)';
    }
  }, [type, suffixColor]);

  const handleDropdownVisibleChange = useCallback(
    (value: boolean) => {
      if (value === false) {
        setTimeout(() => {
          setIsOpen(value);
          onDropdownVisibleChange(value);
        }, 10);
      } else {
        setIsOpen(value);
        onDropdownVisibleChange(value);
      }
    },
    [onDropdownVisibleChange],
  );

  const isDefault = type === 'default';

  useEffect(() => {
    setChecked(value);
  }, [value]);

  useEffect(() => {
    if (defaultValue) setChecked(defaultValue);
  }, [defaultValue]);

  const valueProps = useMemo(() => {
    if (loading || !value) return {};
    if (mode === 'tags' && Array.isArray(value) && value.some(item => !item))
      return {};
    return { value };
  }, [loading, value, mode]);

  const handleChange = useCallback(
    (value: any, option: DefaultOptionType | DefaultOptionType[]) => {
      callbackOnChange?.(options.find(item => item.value === value)?.raw);
      onChange?.(value, option);
      setChecked(value);
    },
    [callbackOnChange, onChange, options],
  );

  return (
    <Select
      {...rest}
      {...valueProps}
      style={newStyle}
      mode={mode}
      open={isOpen}
      showSearch={showSearch}
      placement={placement}
      optionLabelProp="label"
      onChange={handleChange}
      defaultValue={defaultValue}
      placeholder={newPlaceHolder}
      popupMatchSelectWidth={popupMatchSelectWidth}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      className={classNames(className, 'custom-select', {
        primary: type === 'primary',
        linkage: type === 'linkage',
        default: isDefault,
        'empty-UI': empty,
        loading,
      })}
      popupClassName={classNames(popupClassName, 'custom-select-popup', {
        primary: type === 'primary',
        linkage: type === 'linkage',
        default: isDefault,
      })}
      suffixIcon={
        loading ? (
          <Spin indicator={<CustomLoadingOutlined />} size="small" />
        ) : (
          suffixIcon || (
            <DropdownArrow
              open={isOpen}
              color={isOpen && !isDefault ? 'white' : color}
            />
          )
        )
      }
      loading={loading}
    >
      {options.map((option, id) => {
        return (
          <Option
            key={option.value + String(id)}
            value={option.value}
            label={option.label}
            disabled={option.disabled}
          >
            <LabelWrapper key={option.value}>
              {type !== 'default' && (
                <RadioWrapper
                  className={classNames({
                    checked:
                      mode === 'multiple'
                        ? checked?.toString().includes(option.value as any)
                        : option.value === checked,
                    primary: isDefault || type === 'primary',
                  })}
                />
              )}
              {option.label}
            </LabelWrapper>
            {option.description && <div>{option.description}</div>}
          </Option>
        );
      })}
    </Select>
  );
};

export default CustomSelect;
